/**
 * Region-aware currency and date formatting for the school portal.
 *
 * WHY THIS EXISTS
 * The portal hard-codes a pound sign in 19 places across five files and
 * `en-GB` in another 12 — including the field an instructor types their own
 * hourly rate into. For a US instructor every one of those is wrong, and wrong
 * about money, which is the fastest way to lose the person whose cooperation
 * the whole model depends on.
 *
 * The region comes from the school (`FlightSchool.region`, set at onboarding
 * and inherited by its instructors and students). It is never guessed from the
 * browser: a UK school's instructor working from Spain still bills in pounds.
 */

export const REGIONS = {
  UK: { key: 'UK', label: 'United Kingdom', currency: 'GBP', symbol: '£', locale: 'en-GB' },
  US: { key: 'US', label: 'United States', currency: 'USD', symbol: '$', locale: 'en-US' },
  KE: { key: 'KE', label: 'Kenya', currency: 'KES', symbol: 'KSh ', locale: 'en-KE' },
};

// Everything that predates the US build is a UK school.
export const DEFAULT_REGION = 'UK';

const region = (r) => REGIONS[r] || REGIONS[DEFAULT_REGION];

export const currencySymbol = (r) => region(r).symbol;
export const currencyCode = (r) => region(r).currency;
export const localeFor = (r) => region(r).locale;

/**
 * Money, formatted for the region. Falls back to a plain symbol-prefixed number
 * if Intl is unavailable rather than rendering "undefined".
 */
export function formatMoney(amount, r, { decimals = 2 } = {}) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${currencySymbol(r)}0.00`;
  try {
    return new Intl.NumberFormat(localeFor(r), {
      style: 'currency',
      currency: currencyCode(r),
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(n);
  } catch (e) {
    return `${currencySymbol(r)}${n.toFixed(decimals)}`;
  }
}

/** Date, formatted for the region. US reads month-first; UK reads day-first. */
export function formatDate(value, r, opts = { day: 'numeric', month: 'short' }) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(localeFor(r), opts);
}

export function formatDateTime(value, r, opts = { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(localeFor(r), opts);
}

/**
 * Resolve the region from whatever school-shaped object is to hand. Explicit
 * rather than clever: if the caller has no school, it is UK, which is what
 * every existing row is.
 */
export function regionOf(school) {
  return REGIONS[school?.region] ? school.region : DEFAULT_REGION;
}

/**
 * What an instructor is called, in the region's own words.
 *
 * "CFI" is an FAA certificate — Certificated Flight Instructor. The UK has no
 * such rank: the CAA issues an FI, and in ordinary use a UK instructor is just
 * "an instructor". Portal copy was written US-first and says CFI throughout,
 * which reads as jargon to the UK schools who are most of the book and all of
 * the history.
 *
 * Lower case by default because these words nearly always land mid-sentence
 * ("Ask a CFI", "ask an instructor"); pass { caps: true } when one starts one.
 */
export function instructorNoun(r, { caps = false, plural = false } = {}) {
  const us = String(r).toUpperCase() === 'US';
  const word = us
    ? (plural ? 'CFIs' : 'CFI')
    : (plural ? 'instructors' : 'instructor');
  if (!caps) return word;
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** "a CFI" / "an instructor" — the article changes with the word. */
export function anInstructor(r) {
  return String(r).toUpperCase() === 'US' ? 'a CFI' : 'an instructor';
}
